import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"
import { cn } from "../../lib/utils"

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverAnchor = PopoverPrimitive.Anchor

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, ...props }, ref) => {
  /*
   * Fix: We need to render the PopoverContent inside the scaled wrapper (if it exists)
   * so that the popover scales with the rest of the UI.
   * If we don't do this, Radix renders it in document.body by default,
   * which is outside our transform:scale, causing it to look huge (unscaled)
   * relative to the scaled UI.
   */
  const portalContainer = typeof document !== 'undefined' ? document.getElementById('scaled-portal-root') : null

  return (
    <PopoverPrimitive.Portal container={portalContainer || document.body}>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "z-50 w-72 rounded-md border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  )
})
PopoverContent.displayName = PopoverPrimitive.Content.displayName

const PopoverHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex w-full flex-col gap-1 rounded-t-md border-b border-white/10 px-3 py-2",
      className
    )}
    {...props}
  />
))
PopoverHeader.displayName = "PopoverHeader"

const PopoverBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn("p-3", className)} {...props}>
    {children}
  </div>
))
PopoverBody.displayName = "PopoverBody"

const PopoverFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "mt-auto grid w-full gap-2 rounded-b-md border-t border-white/10 px-3 py-2",
      className
    )}
    {...props}
  />
))
PopoverFooter.displayName = "PopoverFooter"

export { Popover, PopoverTrigger, PopoverAnchor, PopoverContent, PopoverHeader, PopoverBody, PopoverFooter }
